import { Component, inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import {
	AgrupamentoEspecial,
	DadosDesvioRelatorio,
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
	RelatorioDesvio,
} from '@home/submodulos/dados-meteorologicos/interfaces';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { Subject, takeUntil, forkJoin } from 'rxjs';

import { FormControl, Validators } from '@angular/forms';
import { endOfMonth, format } from 'date-fns';
import { renderCoordenada, renderNumeroBR } from '@utils';
import { enumAsSelectOptions } from '@utils';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { DateTimeUtils } from '@utils/datetime-util';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services';
import { Select } from '@layout/interfaces/select';
import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';
import { configsColunasCoordenadas } from '../../../utils/configs-colunas-exportacao';

@Component({
	selector: 'seira-tabela-acumulados-desvio-por-periodo',
	templateUrl: './tabela-acumulados-desvio-por-periodo.component.html',
	styleUrls: ['./tabela-acumulados-desvio-por-periodo.component.scss'],
})
export class TabelaAcumuladosDesvioPorPeriodoComponent
	implements OnInit, InstanciaRelatorio, OnDestroy
{
	inputs = inject(INPUTS_RELATORIOS);
	resultado: any[] = [];
	colunas: ADTColumns[] = [];
	carregando = false;
	periodo = '';
	tituloTabela = '';
	periodoAgrupamento = '';
	mesSelecionadoCabecalho = '';
	anoSelecionadoCabecalho = '';
	colunasAnuaisInicializadas = false;
	periodoAnualParcialCabecalho = '';

	texto =
		'Define-se como desvio (mm), a diferença entre a pluviometria registrada e a  sua climatologia para um mesmo período, em um determinado local ou  região. Já o desvio (%) representa a variação da pluviometria em relação à  climatologia, expresso em porcentagem, indicando se o acumulado de chuva  foi superior ou inferior à climatologia.';
	colunasSelecionadas: string[] = [];
	@ViewChild('tabelaDesvioClimatologiaObservado', { static: false })
	tabela?: PublicTableComponent;
	// colunas que existem em TODOS os períodos
	opcoesColunasBase = [
		// localização
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },

		// dados MENSAIS / padrão
		{ name: 'Pluviometria observada (mm)', isFixed: false },
		{ name: 'Climatologia (mm)', isFixed: false },
		{ name: 'Desvio (mm)', isFixed: false },
		{ name: 'Desvio (%)', isFixed: false },
	];

	// colunas que só fazem sentido no modo mensal_anual_parcial
	opcoesColunasAnual = [
		{ name: 'Pluviometria observada anual (mm)', isFixed: false },
		{ name: 'Climatologia anual (mm)', isFixed: false },
		{ name: 'Desvio anual (mm)', isFixed: false },
		{ name: 'Desvio anual (%)', isFixed: false },
	];

	// esta é a lista que o template usa
	opcoesColunas = [...this.opcoesColunasBase];

	incluirDetalhes = false;

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.resultado,
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda:
						this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO) ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					configsColunas: [
						...configsColunasCoordenadas,
						{ coluna: 'desvioMilimetro', casasDecimais: 1 },
						{ coluna: 'desvioPorcentagem', casasDecimais: 1 },
						{ coluna: 'precipitacaoAcumulada', casasDecimais: 1 },
						{ coluna: 'climatologia', casasDecimais: 1 },
					],
					colunasAEsquerda:
						this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO) ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
	];

	agrupamento: Select<string>[];

	_destroyed = new Subject();
	tituloColunaAgrupamento = '';
	disabledSelectList = false;
	tipoDesvio = '';

	opcoesMunPostos: Select<string>[];

	colunasLocalizacao: string[] = [
		'Latitude',
		'Longitude',
		'Altitude',
		'Microrregião',
		'Mesorregião',
		'Região Pluviométrica',
		'Bacia/Sub-bacia',
	];

	constructor(private readonly relatoriosService: RelatoriosService) {
		this.setValidators();

		// só as colunas de DADOS começam ligadas (pluvio, climatologia, desvio...)
		const todasBase = this.opcoesColunasBase.map(op => op.name);
		this.colunasSelecionadas = todasBase.filter(
			nome => !this.colunasLocalizacao.includes(nome)
		);

		this.opcoesMunPostos = this.inputs.municipioPostos;
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit(): void {
		if (!this.inputs.form.get('MES_REFERENCIA_MENSAL_ANUAL')) {
			this.inputs.form.addControl(
				'MES_REFERENCIA_MENSAL_ANUAL',
				new FormControl({ value: null, disabled: true })
			);
		}

		// sempre que mudar Mês inicial/final ou o período, atualiza min/max e habilita/desabilita
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe(() => {
				this.atualizarEstadoMesReferenciaControl();
				this.ajustarMesReferenciaParaIntervalo();
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe(() => {
				this.atualizarEstadoMesReferenciaControl();
				this.ajustarMesReferenciaParaIntervalo();
			});

		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe(() => {
				this.atualizarEstadoMesReferenciaControl();
				this.ajustarMesReferenciaParaIntervalo();
			});

		this.mesReferenciaMensalAnualControl.valueChanges
			.pipe(takeUntil(this._destroyed))
			.subscribe(() => this.ajustarMesReferenciaParaIntervalo());

		// estado inicial
		this.atualizarEstadoMesReferenciaControl();
		this.ajustarMesReferenciaParaIntervalo();
		if (!this.inputs.form.get('MES_REFERENCIA_MENSAL_ANUAL')) {
			this.inputs.form.addControl(
				'MES_REFERENCIA_MENSAL_ANUAL',
				new FormControl(null) // se quiser, depois coloca Validators.required
			);
		}

		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);

		this.tipoDesvio =
			this.inputs.form.get(FormularioRelatorio.TIPO)?.value ===
			'DESVIO_POR_PERIODO_MM'
				? 'milimetro'
				: 'porcentagem';

		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();

		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);

		this.tipoDesvio =
			this.inputs.form.get(FormularioRelatorio.TIPO)?.value ===
			'DESVIO_POR_PERIODO_MM'
				? 'milimetro'
				: 'porcentagem';

		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	ngOnDestroy(): void {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });
		this.clearValidators();
		this._destroyed.next(undefined);
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
		this.gerarRelatorio();
	}

	atualizarTituloColuna() {
		const agrupamento = this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO);
		this.tituloColunaAgrupamento =
			AgrupamentoEspecial[agrupamento as keyof typeof AgrupamentoEspecial];
	}
	private atualizarOpcoesColunasPorPeriodo() {
		if (this.periodoAgrupamento === 'mensal_anual_parcial') {
			// mostra base + anuais no popup
			this.opcoesColunas = [
				...this.opcoesColunasBase,
				...this.opcoesColunasAnual,
			];

			// só NA PRIMEIRA VEZ que entrar nesse modo eu ligo as anuais por padrão
			if (!this.colunasAnuaisInicializadas) {
				const selecionadasSet = new Set(this.colunasSelecionadas);
				this.opcoesColunasAnual.forEach(op => {
					if (!selecionadasSet.has(op.name)) {
						this.colunasSelecionadas.push(op.name);
					}
				});
				this.colunasAnuaisInicializadas = true;
			}
		} else {
			// volta pra lista base (sem anuais)
			this.opcoesColunas = [...this.opcoesColunasBase];

			// remove da seleção qualquer coluna que seja "só anual"
			const baseNames = new Set(this.opcoesColunasBase.map(op => op.name));
			this.colunasSelecionadas = this.colunasSelecionadas.filter(nome =>
				baseNames.has(nome)
			);

			// reseta o flag pra, se voltar pra mensal_anual_parcial, ligar tudo de novo
			this.colunasAnuaisInicializadas = false;
		}
	}

	renderData(data: any) {
		const dataCorrigida = String(data).replace(/\.0/g, '');
		const soData = dataCorrigida.split('T')[0];

		if (/^\d{4}$/.test(soData)) return soData;
		return DateTimeUtils.formatarData(soData, 'MM/yyyy');
	}

	atualizarColunasTabela() {
		const agrupamento = this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO);

		if (agrupamento === 'MUNICIPIO_POSTO') {
			this.disabledSelectList = false;

			const todasColunasDisponiveis = [
				{
					data: 'nome',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
				},
				{
					data: 'latitude',
					title: 'Latitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},
				{
					data: 'longitude',
					title: 'Longitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},
				{
					data: 'altitude',
					title: 'Altitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor, 1),
				},
				{
					data: 'microrregiao',
					title: 'Microrregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'mesorregiao',
					title: 'Mesorregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'regiao',
					title: 'Região Pluviométrica',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'bacia',
					title: 'Bacia/Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'subbacia',
					title: 'Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
			];

			const colunasParaExibir = todasColunasDisponiveis.filter(
				(coluna, index) => {
					if (index === 0) return true; // "Município/Posto" sempre visível
					return this.colunasSelecionadas.includes(coluna.title);
				}
			);

			this.colunas = [...colunasParaExibir];
		} else {
			this.disabledSelectList = true;
			this.colunas = [
				{
					data: 'nome',
					title: this.tituloColunaAgrupamento,
					type: 'string',
					className: 'text-center',
				},
			];
		}

		// 👉 MODO ESPECIAL: mensal + anual parcial na mesma linha
		if (this.periodoAgrupamento === 'mensal_anual_parcial') {
			// PARTE MENSAL
			if (this.colunasSelecionadas.includes('Pluviometria observada (mm)')) {
				this.colunas.push({
					data: 'precipitacaoMensal',
					title: `${this.mesSelecionadoCabecalho || 'Mês/ano'} (mm)`,

					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Climatologia (mm)')) {
				this.colunas.push({
					data: 'climatologiaMensal',
					title: 'Climatologia (mm)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Desvio (mm)')) {
				this.colunas.push({
					data: 'desvioMensal',
					title: 'Desvio (mm)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Desvio (%)')) {
				this.colunas.push({
					data: 'desvioPorcentagem',
					title: 'Desvio (%)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			// PARTE ANUAL
			if (
				this.colunasSelecionadas.includes('Pluviometria observada anual (mm)')
			) {
				this.colunas.push({
					data: 'precipitacaoAnual',
					title: `${
						this.periodoAnualParcialCabecalho ||
						`Ano ${this.anoSelecionadoCabecalho || ''}`
					} (mm)`,

					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Climatologia anual (mm)')) {
				this.colunas.push({
					data: 'climatologiaAnual',
					title: 'Climatologia (mm)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Desvio anual (%)')) {
				this.colunas.push({
					data: 'desvioPercentualAnual',
					title: 'Desvio (%)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			if (this.colunasSelecionadas.includes('Desvio anual (mm)')) {
				this.colunas.push({
					data: 'desvioMilimetroAnual',
					title: 'Desvio (mm)',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				});
			}

			return; // não cai no modo padrão
		}

		// 👉 MODO PADRÃO (diário/mensal/anual/anual_parcial)
		this.colunas.push({
			data: 'data',
			title: 'Data',
			className: 'text-start',
		});

		if (this.colunasSelecionadas.includes('Pluviometria observada (mm)')) {
			this.colunas.push({
				data: 'precipitacaoAcumulada',
				title: 'Pluviometria observada (mm)',
				className: 'text-center',
				render: (valor: string) => renderNumeroBR(valor),
			});
		}

		if (this.colunasSelecionadas.includes('Climatologia (mm)')) {
			this.colunas.push({
				data: 'climatologia',
				title: 'Climatologia (mm)',
				className: 'text-center',
				render: (valor: string) => renderNumeroBR(valor),
			});
		}

		if (this.colunasSelecionadas.includes('Desvio (mm)')) {
			this.colunas.push({
				data: 'desvioMilimetro',
				title: 'Desvio (mm)',
				className: 'text-center',
				render: (valor: string) => renderNumeroBR(valor),
			});
		}

		if (this.colunasSelecionadas.includes('Desvio (%)')) {
			this.colunas.push({
				data: 'desvioPorcentagem',
				title: 'Desvio (%)',
				className: 'text-center',
				render: (valor: string) => renderNumeroBR(valor),
			});
		}
	}

	clearValidators(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	extrairDatasResultado(preciptacaoAgrupada: RelatorioDesvio) {
		return [
			...new Set(
				preciptacaoAgrupada.desvios
					.filter((el: any) => el.data)
					.map((el: any) => {
						const [ano, mes, dia] = el.data!.split('-');
						return new Date(+ano, (+mes || 1) - 1, +dia || 1);
					})
					.sort((a: any, b: any) => +a - +b)
					.map((data: any) => {
						let formato = 'MM/yyyy';
						const isPeriodoAnual = ['anual', 'anual_parcial'].includes(
							this.periodoAgrupamento
						);

						if (isPeriodoAnual) {
							formato = 'yyyy';
						}
						return format(data, formato);
					})
			),
		];
	}
	gerarRelatorio(): void {
		this.carregando = true;
		this.periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;
		this.atualizarOpcoesColunasPorPeriodo();
		const dataInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const dataFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;

		// mensal_anual_parcial NÃO entra como anual
		const isPeriodoAnual = ['anual', 'anual_parcial'].includes(
			this.periodoAgrupamento
		);

		const diaInicio = !isPeriodoAnual
			? dataInicio
			: new Date(`${new Date(dataInicio).getFullYear()}-01-01T00:00:00`);

		const diaFim = !isPeriodoAnual
			? dataFim
			: new Date(`${new Date(dataFim).getFullYear()}-12-31T23:59:59`);

		this.periodo =
			this.periodoAgrupamento === 'mensal' ||
			this.periodoAgrupamento === 'mensal_anual_parcial'
				? DateTimeUtils.formatarDataPeriodoSemDia(
						diaInicio,
						diaFim,
						'mensal'
				  )
				: DateTimeUtils.formatarDataPeriodo(
						diaInicio,
						diaFim,
						this.periodoAgrupamento
				  );
		this.tituloTabela = `Pluviometria - Acumulados e desvios por período - ${this.periodo}`;

		const agrupamentoForm = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		let id = null;

		if (
			!diaInicio ||
			!diaFim ||
			!agrupamentoForm ||
			!this.periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			this.carregando = false;
			return;
		}

		// vamos guardar essas infos pra passar pro método especial
		let anoReferenciaMensal = 0;
		let labelMesReferencia = '';

		if (this.periodoAgrupamento === 'mensal_anual_parcial') {
			const mesRefControl = this.inputs.form.get('MES_REFERENCIA_MENSAL_ANUAL');
			const valorMesRef = mesRefControl?.value;

			let dataReferencia: Date;

			if (valorMesRef) {
				const [anoStr, mesStr] = String(valorMesRef).split('-');
				const anoNum = Number(anoStr);
				const mesNum = Number(mesStr);
				dataReferencia = new Date(anoNum, mesNum - 1, 1);
			} else {
				dataReferencia = new Date(diaFim);
			}

			labelMesReferencia = format(dataReferencia, 'MM/yyyy');
			anoReferenciaMensal = dataReferencia.getFullYear();

			this.mesSelecionadoCabecalho = labelMesReferencia;
			this.anoSelecionadoCabecalho = String(anoReferenciaMensal);

			const dataInicioFiltro = new Date(diaInicio);
			const dataFimFiltro = new Date(diaFim);
			const fimAnualParcial = endOfMonth(dataFimFiltro);

			this.periodoAnualParcialCabecalho = `${format(
				dataInicioFiltro,
				'MM/yyyy'
			)} a ${format(fimAnualParcial, 'MM/yyyy')}`;
		} else {
			this.mesSelecionadoCabecalho = '';
			this.anoSelecionadoCabecalho = '';
			this.periodoAnualParcialCabecalho = '';
		}

		this.incluirDetalhes = false;
		switch (agrupamentoForm) {
			case 'MUNICIPIO_POSTO':
				id = this.inputs.form.get('estacao')?.value;
				this.incluirDetalhes = true;
				break;
			case 'MICRORREGIAO':
				id = this.inputs.form.get('microrregiao')?.value;
				break;
			case 'MESORREGIAO':
				id = this.inputs.form.get('mesorregiao')?.value;
				break;
			case 'REGIAO_PLUVIOMETRICA':
				id = this.inputs.form.get('regiao')?.value;
				break;
			case 'BACIA_SUBBACIA':
				id = this.inputs.form.get('bacia')?.value;
				break;
			case 'MUNICIPIO':
				id = this.inputs.form.get('municipio')?.value;
				break;
		}

		// Para os agrupamentos regionais (Microrregião, Mesorregião, Bacia, Região Pluviométrica),
		// a API não aceita id=0 para retornar o estado completo, exigindo que o parâmetro 'id' seja omitido.
		// Definir id como null faz com que o limparQueryParams remova automaticamente o campo da query string HTTP.
		if (id === '0' || id === 0) {
			id = null;
		}

		this.inputs.setLoading(true);

		const agrupamentoNormalizado = this.normalizarAgrupamento(agrupamentoForm);

		// 🔹 CASO ESPECIAL: Mensal + Anual parcial → 2 chamadas (MENSAL + ANUAL_PARCIAL)
		if (this.periodoAgrupamento === 'mensal_anual_parcial') {
			this.gerarRelatorioMensalAnualParcial(
				agrupamentoNormalizado,
				id,
				diaInicio,
				diaFim,
				anoReferenciaMensal,
				labelMesReferencia
			);
			return;
		}

		// 🔹 CASOS NORMAIS: diário / mensal / anual / anual_parcial (uma chamada só)
		const periodoApi = this.periodoAgrupamento.toUpperCase();

		this.relatoriosService
			.buscarRelatorioDesvio({
				agrupamento: agrupamentoForm,
				idEntidade: id,
				dataFim: new Date(diaFim),
				dataInicio: new Date(diaInicio),
				periodo: periodoApi,
				incluirDetalhes: this.incluirDetalhes,
			})
			.subscribe({
				next: listaDesviosClimasObs => {
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						listaDesviosClimasObs
					);
					this.resultado = [];
					this.inputs.setLoading(false);
					this.carregando = false;

					this.atualizarTituloColuna();
					this.atualizarColunasTabela();

					if (!listaDesviosClimasObs.length) return;

					const dataResultado = this.extrairDatasResultado(
						listaDesviosClimasObs[0]
					);

					listaDesviosClimasObs.forEach(p => {
						// anual / anual_parcial → usa a mesma lógica já existente
						if (['anual', 'anual_parcial'].includes(this.periodoAgrupamento)) {
							const dadosAnuais = this.calcularDadosAnuais(
								p,
								dataResultado,
								agrupamentoNormalizado
							);
							this.resultado.push(...dadosAnuais);
							return;
						}

						// diário / mensal
						const dadosMensais = this.formatarResultados(
							p,
							agrupamentoNormalizado
						);
						this.resultado.push(...dadosMensais);
					});
				},
				error: () => {
					this.inputs.setLoading(false);
					this.carregando = false;
				},
			});
	}
	private gerarRelatorioMensalAnualParcial(
		agrupamentoNormalizado: string,
		id: any,
		diaInicio: Date,
		diaFim: Date,
		anoReferenciaMensal: number,
		labelMesReferencia: string
	) {
		const paramsBase = {
			agrupamento: this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value,
			idEntidade: id,
			dataInicio: new Date(diaInicio),
			dataFim: new Date(diaFim),
			incluirDetalhes: this.incluirDetalhes,
		};

		// 🔸 Chama backend duas vezes: MENSAL + ANUAL_PARCIAL
		forkJoin({
			mensal: this.relatoriosService.buscarRelatorioDesvio({
				...paramsBase,
				periodo: 'MENSAL',
			}),
			anualParcial: this.relatoriosService.buscarRelatorioDesvio({
				...paramsBase,
				periodo: 'ANUAL_PARCIAL',
			}),
		}).subscribe({
			next: ({ mensal, anualParcial }) => {
				// usa o mensal pra controlar exportação
				this.relatoriosService.atualizaVisibilidadeBotoesExportacao(mensal);
				this.resultado = [];
				this.inputs.setLoading(false);
				this.carregando = false;

				this.atualizarTituloColuna();
				this.atualizarColunasTabela();

				if (!mensal.length) return;

				const anoFiltro = anoReferenciaMensal;
				const labelMesFiltro = labelMesReferencia;

				// 🔸 monta um mapa [chave agrupamento] → dados anuais (iguais ao relatório "Anual parcial")
				const mapaAnual = new Map<string, any>();

				if (anualParcial?.length) {
					const anosFiltro = [anoFiltro.toString()];

					anualParcial.forEach(dtoAnual => {
						const chave = this.getChaveAgrupamento(
							dtoAnual,
							agrupamentoNormalizado
						);

						const dadosAno = this.calcularDadosAnuais(
							dtoAnual,
							anosFiltro,
							agrupamentoNormalizado
						)[0];

						if (dadosAno) {
							mapaAnual.set(chave, dadosAno);
						}
					});
				}

				// 🔸 para cada posto/município: junta mês escolhido + anual_parcial do backend
				mensal.forEach(dtoMensal => {
					const chave = this.getChaveAgrupamento(
						dtoMensal,
						agrupamentoNormalizado
					);

					const linhasMensais = this.formatarResultados(
						dtoMensal,
						agrupamentoNormalizado
					);

					// mês escolhido (baseado na data final do filtro)
					const linhaMes =
						linhasMensais.find(l => l.data === labelMesFiltro) ||
						linhasMensais[linhasMensais.length - 1];

					const dadosAno = mapaAnual.get(chave);

					const detalhes =
						agrupamentoNormalizado === 'municipio_posto'
							? this.obterDetalhesPosto(dtoMensal)
							: {
									nome: dtoMensal[
										agrupamentoNormalizado as keyof RelatorioDesvio
									],
							  };

					const registro = {
						...detalhes,
						// coluna de MÊS
						precipitacaoMensal: linhaMes?.precipitacaoAcumulada ?? null,
						climatologiaMensal: linhaMes?.climatologia ?? null,
						desvioMensal: linhaMes?.desvioMilimetro ?? null,
						desvioPorcentagem: linhaMes?.desvioPorcentagem ?? null,
						// colunas ANUAIS (idênticas ao relatório "Anual parcial")
						precipitacaoAnual: dadosAno?.precipitacaoAcumulada ?? null,
						climatologiaAnual: dadosAno?.climatologia ?? null,
						desvioMilimetroAnual: dadosAno?.desvioMilimetro ?? null,
						desvioPercentualAnual: dadosAno?.desvioPorcentagem ?? null,
					};

					this.resultado.push(registro);
				});
			},
			error: () => {
				this.inputs.setLoading(false);
				this.carregando = false;
			},
		});
	}
	private getChaveAgrupamento(
		dto: RelatorioDesvio,
		agrupamentoNormalizado: string
	): string {
		if (agrupamentoNormalizado === 'municipio_posto') {
			const municipio = (dto as any).municipio ?? '';
			const estacao = (dto as any).estacao ?? '';
			return `${municipio}||${estacao}`;
		}

		const valor = dto[agrupamentoNormalizado as keyof RelatorioDesvio];
		return String(valor ?? '');
	}

	private parseDataOrdenacao(valor: string): number {
		if (/^\d{4}$/.test(valor)) return new Date(Number(valor), 0, 1).getTime();

		const m = /^(\d{2})\/(\d{4})$/.exec(valor);
		if (m) return new Date(Number(m[2]), Number(m[1]) - 1, 1).getTime();

		const d = new Date(valor);
		return isNaN(d.getTime()) ? 0 : d.getTime();
	}

	private calcularDadosMensaisEAnuais(
		resultadoDesvios: RelatorioDesvio,
		dataResultado: string[],
		agrupamento: string
	) {
		// Dados mensais
		const dadosMensais = this.formatarResultados(resultadoDesvios, agrupamento);

		// Dados anuais parciais
		const anosUnicos = [
			...new Set(dataResultado.map(data => data.split('/')[1] || data)),
		];
		const dadosAnuais = this.calcularDadosAnuais(
			resultadoDesvios,
			anosUnicos,
			agrupamento
		);

		// Combinar e ordenar
		return [...dadosMensais, ...dadosAnuais].sort(
			(a, b) =>
				this.parseDataOrdenacao(a.data) - this.parseDataOrdenacao(b.data)
		);
	}

	private normalizarAgrupamento(agrupamento: string): string {
		agrupamento = agrupamento.toLowerCase();
		if (agrupamento === 'regiao_pluviometrica') {
			return 'regiaoPluviometrica';
		}
		if (agrupamento === 'subbacia') {
			return 'subBacia';
		}
		if (agrupamento === 'bacia_subbacia') {
			return 'bacia';
		}
		return agrupamento;
	}

	private calcularDadosAnuais(
		resultadoDesvios: RelatorioDesvio,
		dataResultado: string[],
		agrupamento: string
	) {
		return dataResultado.map(ano => {
			let dadosDoAno = resultadoDesvios.desvios.filter((item: any) =>
				item.data.startsWith(ano)
			);

			const currentSystemDate = new Date();
			const currentSystemYear = currentSystemDate.getFullYear();
			const currentSystemMonth = currentSystemDate.getMonth();

			// mantém apenas meses até o mês atual quando o ano é o atual
			if (parseInt(ano, 10) === currentSystemYear) {
				dadosDoAno = dadosDoAno.filter((item: any) => {
					const itemDateParts = item.data.split('-');
					if (itemDateParts.length < 2) return false;
					const itemMonth = parseInt(itemDateParts[1], 10) - 1;
					return itemMonth <= currentSystemMonth;
				});
			}

			const desvios = this.extrairDesviosDeDTOAnual(dadosDoAno);

			const detalhes =
				agrupamento === 'municipio_posto'
					? this.obterDetalhesPosto(resultadoDesvios)
					: {};

			return {
				nome: resultadoDesvios[agrupamento as keyof RelatorioDesvio],

				...desvios,
				...detalhes,
				data: ano,
			};
		});
	}
	private extrairDesviosDeDTOAnual(dados: DadosDesvioRelatorio[]) {
		if (!dados?.length) {
			return {
				precipitacaoAcumulada: null,
				climatologia: null,
				desvioMilimetro: null,
				desvioPorcentagem: null,
			};
		}

		const { chuva, ...resto } = dados[0] as any;
		const valorChuva = chuva ?? resto.precipitacaoAcumulada;
		return {
			...resto,
			precipitacaoAcumulada:
				valorChuva != null ? Number(valorChuva).toFixed(1) : null,
			climatologia:
				resto.climatologia != null
					? Number(resto.climatologia).toFixed(1)
					: null,
		};
	}

	private obterDetalhesPosto(dadosPosto: any) {
		return {
			nome:
				dadosPosto.municipio === dadosPosto.estacao
					? dadosPosto.municipio
					: `${dadosPosto.municipio}/${dadosPosto.estacao}`,
			municipio: dadosPosto.municipio || '-',
			latitude: dadosPosto.latitude,
			longitude: dadosPosto.longitude,
			altitude: dadosPosto.altitude,
			microrregiao: dadosPosto.microrregiao || '-',
			mesorregiao: dadosPosto.mesorregiao || '-',
			regiao: dadosPosto.regiaoPluviometrica || '-',
			bacia: dadosPosto.bacia || '-',
		};
	}

	private formatarResultados(resultados: RelatorioDesvio, agrupamento: string) {
		return resultados.desvios.map((resultado: any) => {
			const valorChuva = resultado.chuva ?? resultado.precipitacaoAcumulada;
			return {
				...(agrupamento === 'municipio_posto'
					? this.obterDetalhesPosto(resultados)
					: { nome: resultados[agrupamento as keyof RelatorioDesvio] }),
				data: this.renderData(resultado.data),
				desvioMilimetro: resultado.desvioMilimetro,
				desvioPorcentagem: resultado.desvioPorcentagem,
				climatologia:
					resultado.climatologia != null
						? Number(resultado.climatologia).toFixed(1)
						: null,
				precipitacaoAcumulada:
					valorChuva != null ? Number(valorChuva).toFixed(1) : null,
			};
		});
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)!.value;
	}
	private calcularAnuaisParciais(dto: RelatorioDesvio, agrupamento: string) {
		// agrupa os desvios por ano a partir do campo data (Date ou string)
		const porAno = new Map<string, DadosDesvioRelatorio[]>();

		dto.desvios.forEach(d => {
			if (!d.data) return;

			// garante um Date válido independente se veio Date ou string
			const dataObj = d.data instanceof Date ? d.data : new Date(d.data as any);

			if (isNaN(dataObj.getTime())) return;

			const ano = dataObj.getFullYear().toString();

			if (!porAno.has(ano)) porAno.set(ano, []);
			porAno.get(ano)!.push(d);
		});

		const detalhes =
			agrupamento === 'municipio_posto'
				? this.obterDetalhesPosto(dto)
				: { nome: dto[agrupamento as keyof RelatorioDesvio] };

		const anuais: any[] = [];

		porAno.forEach((desviosAno, ano) => {
			const precipitacaoAcumulada = desviosAno.reduce(
				(acc, item) => acc + (item.chuva ?? 0),
				0
			);

			const climatologia = desviosAno.reduce(
				(acc, item) => acc + (item.climatologia ?? 0),
				0
			);

			const desvioMilimetro = precipitacaoAcumulada - climatologia;
			const desvioPorcentagem =
				climatologia !== 0 ? (desvioMilimetro / climatologia) * 100 : null;

			anuais.push({
				...detalhes,
				data: ano,
				precipitacaoAcumulada,
				climatologia,
				desvioMilimetro,
				desvioPorcentagem,
			});
		});

		return anuais;
	}
	get mesReferenciaMensalAnualControl(): FormControl {
		return this.inputs.form.get('MES_REFERENCIA_MENSAL_ANUAL') as FormControl;
	}
	get mesReferenciaMin(): string | null {
		const v = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.value;
		if (!v) return null;
		return format(new Date(v), 'yyyy-MM');
	}

	get mesReferenciaMax(): string | null {
		const v = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		if (!v) return null;
		return format(new Date(v), 'yyyy-MM');
	}

	private ajustarMesReferenciaParaIntervalo() {
		const min = this.mesReferenciaMin;
		const max = this.mesReferenciaMax;
		const ctrl = this.mesReferenciaMensalAnualControl;

		if (!min || !max || ctrl.disabled) return;

		const valor = ctrl.value as string | null;

		if (!valor) {
			ctrl.setValue(max, { emitEvent: false });
			return;
		}

		if (valor < min) ctrl.setValue(min, { emitEvent: false });
		if (valor > max) ctrl.setValue(max, { emitEvent: false });
	}

	private atualizarEstadoMesReferenciaControl() {
		const ctrl = this.mesReferenciaMensalAnualControl;

		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;

		const deveAparecer = periodo === 'mensal_anual_parcial';
		const temIntervalo = !!this.mesReferenciaMin && !!this.mesReferenciaMax;

		if (!deveAparecer || !temIntervalo) {
			if (ctrl.enabled) ctrl.disable({ emitEvent: false });
			return;
		}

		if (ctrl.disabled) ctrl.enable({ emitEvent: false });
	}
	get isMensalAnualParcial(): boolean {
		return (
			this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value ===
			'mensal_anual_parcial'
		);
	}
}
